import * as React from "react";
import {ButtonHTMLAttributes, CSSProperties, MutableRefObject, useRef, useState} from "react";
import {disabledButtonStyle} from "./buttonStyles";

export interface IRaisedButton<T> extends ButtonHTMLAttributes<T> {
    style: CSSProperties;
    primary: boolean;
    disabled?: boolean;
}

export const RaisedButton: React.FunctionComponent<IRaisedButton<any>> = (props) => {
    const {style, primary, disabled, id, className, onClick, children, ...restButtonProps} = props;
    const buttonRef: React.MutableRefObject<HTMLButtonElement | null> = useRef(null);
    const [rippleArray, setRippleArray] = useState([]);
    const [hovered, setHovered] = useState(false);
    const [focusVisible, setFocusVisible] = useState(false);
    const hoveredClass = () => hovered ? "highlight" : "";
    const accentColor = () => primary ? "white" : "black";
    const accentStyle: CSSProperties = { "--overlay-color": accentColor() } as CSSProperties;

    function createRipple(e: any) {
        const height = buttonRef.current!.clientHeight;
        const rect = e.target!.getBoundingClientRect();
        const newX = e.clientX - rect.left;
        const newY = e.clientY - rect.top;

        return <Ripple key={newX} x={newX} y={newY} height={height} color={accentColor()}/>;
    }

    function handleHover(value: boolean) {
        !disabled ? setHovered(value) : setHovered(false);
    }

    return <div id={id}
                className={`shared-components__raised-button${focusVisible ? " shared-components__raised-button--focus-visible" : ""}`}
                style={{display: "inline-block"}}
                onMouseEnter={() => handleHover(true)}
                onMouseLeave={() => handleHover(false)}>
        <button
            ref={buttonRef}
            className={`base shadow ${className}`}
            style={!disabled ? style : {...style, ...disabledButtonStyle}}
            onMouseDown={(event) => {
                setFocusVisible(false);
                if (!disabled) {
                    const newRip: any = createRipple(event);
                    setRippleArray(rippleArray.concat(newRip).slice());
                }
            }}
            onFocus={(event) => {
                setFocusVisible(event.currentTarget.matches(":focus-visible"));
            }}
            onBlur={() => setFocusVisible(false)}
            onClick={!disabled ? onClick : undefined}
            {...restButtonProps}>
            {children}
            {rippleArray}
            <span style={accentStyle} className={`overlay ${hoveredClass()}`} />
        </button>
    </div>;
};

RaisedButton.displayName = "RaisedButton";

interface IRipple {
    x: number;
    y: number;
    height: number;
    color: string;
}

const Ripple: React.FunctionComponent<IRipple> = ({x, y, height, color}) => {
    const spanRef: MutableRefObject<HTMLSpanElement | null> = useRef(null);

    const offsetX = x - height / 2;
    const offsetY = y - height / 2;
    const style: CSSProperties = {
        left: offsetX,
        top: offsetY,
        width: height,
        transform: "scale(0)",
        "--ripple-color": color,
    } as CSSProperties;

    return <span ref={spanRef} className="ripple" style={style}/>;
};
